import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Key, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

/**
 * Profile-menu Change Password dialog.
 * Reuses the same backend endpoints as the first-login forced flow:
 *   POST /api/me/change-password/request-otp
 *   POST /api/me/change-password  { current_password, new_password, confirm_password, otp }
 */
export default function ChangePasswordDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [step, setStep] = useState("form"); // form -> verify
  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      // reset on close
      setStep("form");
      setCurrent(""); setNewPw(""); setConfirm(""); setOtp("");
      setBusy(false);
    }
  }, [open]);

  const sendOtp = async () => {
    if (!current) { toast.error("Enter your current password"); return; }
    if (!newPw || !confirm) { toast.error("Enter a new password"); return; }
    if (newPw !== confirm) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/me/change-password/request-otp");
      toast.success(`Verification code sent to ${user?.email}`);
      setStep("verify");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send code");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!otp) { toast.error("Enter the verification code"); return; }
    setBusy(true);
    try {
      await api.post("/me/change-password", {
        current_password: current,
        new_password: newPw,
        confirm_password: confirm,
        otp,
      });
      toast.success("Password updated");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2">
            <Key size={20} weight="fill" className="text-emerald-600" /> Change password
          </DialogTitle>
          <DialogDescription>
            {step === "form"
              ? "Enter your current password and choose a new one. We'll email a verification code to confirm the change."
              : `We've sent a 6-digit verification code to ${user?.email}. Enter it below to complete the change.`}
          </DialogDescription>
        </DialogHeader>

        {step === "form" ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Current password</Label>
              <Input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="mt-1 h-11"
                data-testid="cp-current-input"
                autoComplete="current-password"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">New password</Label>
              <Input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="mt-1 h-11"
                data-testid="cp-new-input"
                autoComplete="new-password"
              />
              <div className="text-[11px] text-muted-foreground mt-1">Min 8 chars, with upper, lower, number and symbol.</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Confirm new password</Label>
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 h-11"
                data-testid="cp-confirm-input"
                autoComplete="new-password"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">6-digit verification code</Label>
              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                className="mt-1 h-14 text-center font-mono text-2xl tracking-[0.5em] num-display"
                data-testid="cp-otp-input"
                autoFocus
              />
            </div>
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <ShieldCheck size={12} weight="fill" className="text-emerald-600" />
              Didn&apos;t get the code? <button type="button" onClick={sendOtp} className="text-emerald-700 dark:text-emerald-400 hover:underline font-semibold" data-testid="cp-resend-otp">Resend</button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="cp-cancel-btn">Cancel</Button>
          {step === "form" ? (
            <Button
              onClick={sendOtp}
              disabled={busy}
              className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
              data-testid="cp-send-otp-btn"
            >
              {busy ? "Sending code…" : "Send verification code"}
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={busy}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="cp-submit-btn"
            >
              {busy ? "Updating…" : "Confirm & update"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
